/**
 * The dialog card of the living screens, on Radix Dialog so focus, Escape and the scrim behave as every Panel dialog.
 * The card is centred and arrives on the pop spring. The scrim is a plain dim: no blur. Under reduced motion it appears
 * and leaves at once. The sheet (`sheet.tsx`) shares this file's overlay props and content props.
 *
 * The Panel's entry draws the card (the shell's dialogs), so this file holds only what the entry uses: the sheet lives
 * in its own file and ships with the chunks that open one.
 *
 * A Radix portal renders outside the screen root, so the card carries the tone of the screen it was opened from.
 */

import * as Dialog from '@radix-ui/react-dialog';
import type { ReactNode } from 'react';

import { toneAttributes, useLivingTone } from './tone';
import './living.scss';

/** What the dialog card and the sheet both take. */
export interface OverlayProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly className?: string;
  readonly testId?: string;
  /** A test id for the scrim (the help dialog of Acceso keeps today's `access-help-backdrop`). */
  readonly overlayTestId?: string;
  /** False keeps Escape and a tap on the scrim from closing it (a step that must be answered). */
  readonly dismissible?: boolean;
  /** Where focus goes when it opens (Radix default: the first focusable control). */
  readonly onOpenAutoFocus?: (event: Event) => void;
  /** Where focus goes back when it closes. */
  readonly onCloseAutoFocus?: (event: Event) => void;
  readonly children?: ReactNode;
}

const keepOpen = (event: Event) => event.preventDefault();

/** Radix content props from the overlay props: dismissal, focus and the test id. */
export function contentProps({ dismissible = true, onOpenAutoFocus, onCloseAutoFocus, testId }: OverlayProps) {
  return {
    ...(dismissible ? {} : { onEscapeKeyDown: keepOpen, onPointerDownOutside: keepOpen, onInteractOutside: keepOpen }),
    ...(onOpenAutoFocus === undefined ? {} : { onOpenAutoFocus }),
    ...(onCloseAutoFocus === undefined ? {} : { onCloseAutoFocus }),
    ...(testId === undefined ? {} : { 'data-testid': testId }),
  };
}

export interface DialogCardProps extends OverlayProps {
  readonly title: ReactNode;
  /** One sentence under the title; Radix links it as the description. */
  readonly description?: ReactNode;
  /** The actions, right-aligned under the body. */
  readonly footer?: ReactNode;
  /**
   * False draws the card without a scrim and without holding the page: no focus trap, no scroll lock, the page under it
   * takes taps and keys, and a tap outside or Escape still closes it (a confirmation that only informs). The keyboard
   * staying on the page does not close it. Default true.
   */
  readonly modal?: boolean;
}

export function DialogCard(props: DialogCardProps) {
  const { open, onOpenChange, title, description, footer, className, children, overlayTestId, modal = true } = props;
  const tone = toneAttributes(useLivingTone());
  return (
    <Dialog.Root onOpenChange={onOpenChange} open={open} modal={modal}>
      <Dialog.Portal>
        <Dialog.Overlay className="lv-scrim" {...tone} {...(overlayTestId === undefined ? {} : { 'data-testid': overlayTestId })} />
        <Dialog.Content {...contentProps(props)} {...(modal ? {} : { onFocusOutside: keepOpen })} {...tone} {...(description === undefined ? { 'aria-describedby': undefined } : {})}
          className={className === undefined ? 'lv-dcard' : `lv-dcard ${className}`}>
          <Dialog.Title className="lv-dcard__title">{title}</Dialog.Title>
          {description === undefined ? null : <Dialog.Description className="lv-dcard__sub">{description}</Dialog.Description>}
          {children === undefined ? null : <div className="lv-dcard__body">{children}</div>}
          {footer === undefined ? null : <footer className="lv-dcard__foot">{footer}</footer>}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** Closes the dialog card or sheet it is inside: `<DialogClose asChild><button …>Cancelar</button></DialogClose>`. */
export const DialogClose = Dialog.Close;
